/**
 * Parsing + validation for spreadsheet / pasted intake.
 *
 * - CSV / TSV are parsed inline (no dependency) with a quote-aware reader.
 * - XLSX / XLS / ODS are parsed by lazily importing SheetJS only when a
 *   binary spreadsheet is actually dropped, so it never weighs on the main
 *   bundle.
 * - Headers are auto-mapped to client fields by fuzzy alias matching.
 * - Mapped rows are coerced and validated against the CreateClientZ contract.
 */

import type {
  ClientDraft,
  ColumnMap,
  FieldKey,
  FieldSpec,
  MappedRow,
  ParsedTable,
} from './types'

// ─── Destination fields + header aliases ─────────────────────────

export const FIELD_SPECS: FieldSpec[] = [
  { key: 'name', label: 'Client name', required: true, aliases: ['name', 'client', 'clientname', 'company', 'companyname', 'business', 'businessname', 'account', 'organization', 'org'] },
  { key: 'vertical', label: 'Vertical', aliases: ['vertical', 'industry', 'niche', 'sector', 'category', 'segment'] },
  { key: 'plan', label: 'Plan', aliases: ['plan', 'tier', 'package', 'product', 'service', 'offering'] },
  { key: 'status', label: 'Status', aliases: ['status', 'state', 'accountstatus'] },
  { key: 'health', label: 'Health', aliases: ['health', 'accounthealth', 'risk'] },
  { key: 'mrr', label: 'MRR', aliases: ['mrr', 'monthlyrevenue', 'monthlyrecurringrevenue', 'revenue', 'monthly', 'value', 'contractvalue', 'amount'] },
  { key: 'website', label: 'Website', aliases: ['website', 'url', 'site', 'web', 'domain', 'homepage'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'description', 'comments', 'comment', 'details', 'about'] },
]

const FIELD_BY_KEY = new Map(FIELD_SPECS.map((f) => [f.key, f]))

const STATUS_VALUES = ['active', 'inactive', 'churned'] as const
const HEALTH_VALUES = ['good', 'watch', 'at-risk'] as const

/** Column labels shown in the mapping dropdown. */
export const FIELD_OPTIONS: { key: FieldKey; label: string }[] = FIELD_SPECS.map((f) => ({
  key: f.key,
  label: f.label + (f.required ? ' *' : ''),
}))

// ─── Delimited text (CSV / TSV) ──────────────────────────────────

/** Pick the most likely delimiter from the header line. */
function detectDelimiter(sample: string): string {
  const firstLine = sample.split(/\r?\n/, 1)[0] ?? ''
  const candidates: [string, number][] = [
    ['\t', (firstLine.match(/\t/g) ?? []).length],
    [',', (firstLine.match(/,/g) ?? []).length],
    [';', (firstLine.match(/;/g) ?? []).length],
    ['|', (firstLine.match(/\|/g) ?? []).length],
  ]
  candidates.sort((a, b) => b[1] - a[1])
  const best = candidates[0]
  return best && best[1] > 0 ? best[0] : ','
}

/** Quote-aware delimited parser (handles "quoted, fields" and "" escapes). */
export function parseDelimited(text: string, delimiter?: string): ParsedTable | null {
  const clean = text.replace(/^﻿/, '') // strip BOM
  if (!clean.trim()) return null
  const delim = delimiter ?? detectDelimiter(clean)

  const rows: string[][] = []
  let field = ''
  let row: string[] = []
  let inQuotes = false

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]
    if (inQuotes) {
      if (ch === '"') {
        if (clean[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delim) {
      row.push(field)
      field = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && clean[i + 1] === '\n') i++
      row.push(field)
      rows.push(row)
      field = ''
      row = []
    } else {
      field += ch
    }
  }
  // flush trailing field/row
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }

  return toTable(rows, 'Pasted / delimited data')
}

/** Turn raw rows into a headers + body table, dropping fully-empty rows. */
function toTable(raw: string[][], source: string): ParsedTable | null {
  const nonEmpty = raw.filter((r) => r.some((c) => String(c ?? '').trim() !== ''))
  const headerRow = nonEmpty[0]
  if (!headerRow) return null
  const headers = headerRow.map((h) => String(h ?? '').trim())
  const rows = nonEmpty.slice(1).map((r) => headers.map((_, i) => String(r[i] ?? '').trim()))
  return { headers, rows, source }
}

// ─── Spreadsheet (XLSX / XLS / ODS) via lazy SheetJS ─────────────

const SPREADSHEET_RE = /\.(xlsx|xlsm|xlsb|xls|ods)$/i

export function isSpreadsheetFile(file: File): boolean {
  return SPREADSHEET_RE.test(file.name)
}

/**
 * Parse an uploaded file into a table. Chooses parser by extension.
 * `sheetHint` picks the first sheet whose NAME matches (workbooks often carry
 * several tabs — e.g. "Team & Roles" + "Clients"); falls back to sheet 1.
 */
export async function parseFile(file: File, opts?: { sheetHint?: RegExp }): Promise<ParsedTable | null> {
  if (isSpreadsheetFile(file)) {
    // Lazy-load SheetJS — keeps ~800KB out of the main bundle.
    const XLSX = await import('xlsx')
    const buf = await file.arrayBuffer()
    const wb = XLSX.read(buf, { type: 'array' })
    const sheetName =
      (opts?.sheetHint && wb.SheetNames.find((n) => opts.sheetHint!.test(n))) ?? wb.SheetNames[0]
    if (!sheetName) return null
    const sheet = wb.Sheets[sheetName]
    if (!sheet) return null
    const raw = XLSX.utils.sheet_to_json<string[]>(sheet, {
      header: 1,
      raw: false,
      defval: '',
      blankrows: false,
    })
    const table = toTable(
      raw.map((r) => (Array.isArray(r) ? r.map((c) => String(c ?? '')) : [])),
      wb.SheetNames.length > 1 ? `${file.name} · ${sheetName}` : file.name,
    )
    return table
  }

  // Everything else (.csv, .tsv, .txt, unknown) → delimited text.
  const text = await file.text()
  const table = parseDelimited(text)
  if (table) table.source = file.name
  return table
}

/**
 * Real-world exports often carry banner/preamble rows above the true header
 * (a sheet title, a band row like "ACCOUNT | TEAM | TASK 1", permission
 * notes, …). Scan the current header plus the first few data rows for the
 * one that actually looks like the header — every `required` name present
 * (normalized) and, when given, at least one of `anyOf` — and re-slice the
 * table from there. Returns the table unchanged if the current header already
 * matches or no better row is found.
 */
export function realignTable(table: ParsedTable, required: string[], anyOf: string[] = []): ParsedTable {
  const matches = (cells: string[]) => {
    const set = new Set(cells.map(normalizeHeader))
    return required.every((r) => set.has(r)) && (anyOf.length === 0 || anyOf.some((a) => set.has(a)))
  }
  if (matches(table.headers)) return table
  const scan = table.rows.slice(0, 10)
  for (let i = 0; i < scan.length; i++) {
    const cand = scan[i]!
    if (!matches(cand)) continue
    const headers = cand.map((h) => String(h ?? '').trim())
    const rows = table.rows.slice(i + 1).map((r) => headers.map((_, ci) => String(r[ci] ?? '').trim()))
    return { headers, rows, source: table.source }
  }
  return table
}

/**
 * Does this table look like the WIDE onboarding sheet (client + team + task
 * columns inline)? The plain client importer ignores those columns, so
 * callers use this to steer the user to the Onboard flow instead.
 */
export function looksLikeOnboardSheet(headers: string[]): boolean {
  const set = headers.map(normalizeHeader)
  return set.some((h) => h === 'accountmanager' || /^teammember/.test(h) || /^task\d+$/.test(h))
}

// ─── Auto-mapping ────────────────────────────────────────────────

function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[\s_\-./]+/g, '').replace(/[^a-z0-9]/g, '')
}

/** Best-effort map from each column index to a client field. */
export function autoMap(headers: string[]): ColumnMap {
  const map: ColumnMap = {}
  const used = new Set<FieldKey>()

  headers.forEach((header, i) => {
    const norm = normalizeHeader(header)
    map[i] = null
    if (!norm) return
    for (const spec of FIELD_SPECS) {
      if (used.has(spec.key)) continue
      const hit =
        spec.aliases.includes(norm) ||
        spec.aliases.some((a) => norm === a || norm.includes(a) || a.includes(norm))
      if (hit) {
        map[i] = spec.key
        used.add(spec.key)
        break
      }
    }
  })

  return map
}

// ─── Coercion + validation ───────────────────────────────────────

function parseMoney(raw: string): number | undefined {
  const cleaned = raw.replace(/[$,\s]/g, '').replace(/\/mo$/i, '')
  if (cleaned === '') return undefined
  const n = Number(cleaned)
  return Number.isFinite(n) ? Math.round(n) : NaN
}

function normalizeStatus(raw: string): ClientDraft['status'] | undefined {
  const v = raw.toLowerCase().trim()
  if (!v) return undefined
  if (STATUS_VALUES.includes(v as any)) return v as ClientDraft['status']
  if (['live', 'current', 'won', 'signed'].includes(v)) return 'active'
  if (['paused', 'dormant', 'on hold'].includes(v)) return 'inactive'
  if (['lost', 'cancelled', 'canceled', 'ended'].includes(v)) return 'churned'
  return undefined
}

function normalizeHealth(raw: string): ClientDraft['health'] | undefined {
  const v = raw.toLowerCase().trim()
  if (!v) return undefined
  if (HEALTH_VALUES.includes(v as any)) return v as ClientDraft['health']
  if (['healthy', 'green', 'ok', 'good'].includes(v)) return 'good'
  if (['watch', 'amber', 'yellow', 'warning'].includes(v)) return 'watch'
  if (['at risk', 'atrisk', 'red', 'danger', 'critical'].includes(v)) return 'at-risk'
  return undefined
}

/**
 * Validate a single draft against the CreateClientZ contract.
 * Returns human-readable error strings (empty = valid).
 */
export function validateDraft(draft: ClientDraft): string[] {
  const errors: string[] = []
  if (!draft.name || !draft.name.trim()) errors.push('Name is required')
  if (draft.mrr !== undefined && (!Number.isInteger(draft.mrr) || draft.mrr < 0)) {
    errors.push('MRR must be a whole number ≥ 0')
  }
  if (draft.website && !/^https?:\/\/.+/i.test(draft.website)) {
    errors.push('Website must start with http:// or https://')
  }
  if (draft.status && !STATUS_VALUES.includes(draft.status)) {
    errors.push(`Status must be one of ${STATUS_VALUES.join(', ')}`)
  }
  if (draft.health && !HEALTH_VALUES.includes(draft.health)) {
    errors.push(`Health must be one of ${HEALTH_VALUES.join(', ')}`)
  }
  return errors
}

/** Coerce one string cell into the typed value for its destination field. */
function coerceCell(key: FieldKey, raw: string, errors: string[]): unknown {
  const value = raw.trim()
  switch (key) {
    case 'mrr': {
      const n = parseMoney(value)
      if (Number.isNaN(n)) {
        errors.push(`MRR "${raw}" is not a number`)
        return undefined
      }
      return n
    }
    case 'status':
      return normalizeStatus(value)
    case 'health':
      return normalizeHealth(value)
    case 'website': {
      if (!value) return undefined
      // Be forgiving: prepend https:// to bare domains.
      return /^https?:\/\//i.test(value) ? value : `https://${value}`
    }
    default:
      return value || undefined
  }
}

/** Apply a column map across a table, producing validated draft rows. */
export function mapRows(table: ParsedTable, colMap: ColumnMap): MappedRow[] {
  return table.rows.map((cells, rowIdx) => {
    const draft: ClientDraft = { name: '' }
    const errors: string[] = []

    cells.forEach((cell, colIdx) => {
      const key = colMap[colIdx]
      if (!key || cell.trim() === '') return
      const coerced = coerceCell(key, cell, errors)
      if (coerced !== undefined) (draft as unknown as Record<string, unknown>)[key] = coerced
    })

    errors.push(...validateDraft(draft))
    return { draft, errors, sourceRow: rowIdx + 2 } // +2: 1 for header, 1 for 1-based
  })
}

/** Does the map cover the required fields? Returns the missing ones. */
export function missingRequired(colMap: ColumnMap): FieldSpec[] {
  const mapped = new Set(Object.values(colMap).filter(Boolean) as FieldKey[])
  return FIELD_SPECS.filter((f) => f.required && !mapped.has(f.key))
}

export { FIELD_BY_KEY }
